import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { OperatorApiService } from '../../../core/operator/operator-api.service';
import { TenantService } from '../../../core/tenant/tenant.service';
import { AuthStateService } from '../../auth/data/auth-state.service';
import { PassengerApiService } from '../../trips/data/passenger-api.service';
import { concatMap, forkJoin, of } from 'rxjs';
import { BookingApiService, starpayError } from '../data/booking-api.service';
import { saveLastBooking } from '../data/last-booking';
import { BookingStateService } from '../data/booking-state.service';
import {
  ConcessionSelection,
  CreateBookingRequest,
  DELIVERY_METHOD_DEFAULT,
  DiscountQuote,
  PAYMENT_METHODS,
  PublicDiscount,
  ScheduleAvailability,
  SeatMapSeat,
} from '../models/booking.models';
import { formatCurrency, formatDateShort, formatTime12 } from '../util/format';

@Component({
  selector: 'app-checkout-page',
  standalone: true,
  imports: [AppIconComponent, RouterLink, FormsModule],
  templateUrl: './checkout.page.html',
  styleUrl: './checkout.page.scss',
})
export class CheckoutPage implements OnInit {
  private readonly bookingApi = inject(BookingApiService);
  private readonly bookingState = inject(BookingStateService);
  private readonly authState = inject(AuthStateService);
  private readonly passengerApi = inject(PassengerApiService);
  private readonly operatorApi = inject(OperatorApiService);
  private readonly tenantService = inject(TenantService);
  private readonly router = inject(Router);

  readonly schedule = this.bookingState.outboundSchedule;
  readonly returnSchedule = this.bookingState.returnSchedule;
  readonly criteria = this.bookingState.criteria;
  readonly selectedSeats = this.bookingState.outboundSeats;
  readonly returnSeats = this.bookingState.returnSeats;
  readonly isRoundTrip = this.bookingState.isRoundTrip;

  readonly paymentMethods = PAYMENT_METHODS;

  readonly firstName = signal('');
  readonly lastName = signal('');
  readonly email = signal('');
  readonly phone = signal('');
  readonly promoCode = signal('');
  readonly promoError = signal('');
  readonly paymentMethod = signal<string>(PAYMENT_METHODS[0].id);
  readonly agreeToTerms = signal(false);
  readonly availableDiscounts = signal<PublicDiscount[]>([]);
  readonly studentPax = signal(0);
  readonly pwdPax = signal(0);
  readonly seniorPax = signal(0);
  readonly quote = signal<DiscountQuote | null>(null);

  readonly passengerId = signal<number | null>(null);
  readonly submitting = signal(false);
  readonly error = signal('');

  readonly formatTime = formatTime12;
  readonly formatCurrency = formatCurrency;

  readonly dateLabel = computed(() => {
    const criteria = this.criteria();
    return criteria ? formatDateShort(criteria.date) : '';
  });

  readonly returnDateLabel = computed(() => {
    const date = this.criteria()?.returnDate;
    return date ? formatDateShort(date) : '';
  });

  readonly outboundSubtotal = computed(() =>
    this.selectedSeats().reduce((sum, seat) => sum + (seat.price ?? 0), 0),
  );
  readonly returnSubtotal = computed(() =>
    this.returnSeats().reduce((sum, seat) => sum + (seat.price ?? 0), 0),
  );
  readonly subtotal = computed(
    () => this.outboundSubtotal() + this.returnSubtotal(),
  );
  readonly seatCount = computed(
    () => this.selectedSeats().length + this.returnSeats().length,
  );
  readonly transactionFee = signal(0);
  readonly appliedPromo = signal('');
  readonly discount = computed(() => this.quote()?.discountAmount ?? 0);
  readonly total = computed(
    () => this.subtotal() - this.discount() + this.transactionFee(),
  );
  readonly minPwd = computed(() =>
    Math.max(
      this.reservedCount(this.selectedSeats(), 'pwd'),
      this.reservedCount(this.returnSeats(), 'pwd'),
    ),
  );
  readonly minSenior = computed(() =>
    Math.max(
      this.reservedCount(this.selectedSeats(), 'senior'),
      this.reservedCount(this.returnSeats(), 'senior'),
    ),
  );

  readonly canPay = computed(
    () =>
      this.firstName().trim().length > 0 &&
      this.lastName().trim().length > 0 &&
      this.agreeToTerms() &&
      !this.submitting(),
  );

  ngOnInit(): void {
    const outbound = this.schedule();
    const criteria = this.criteria();
    if (!outbound || !criteria || this.selectedSeats().length === 0) {
      this.router.navigate(['/']);
      return;
    }

    if (
      this.isRoundTrip() &&
      (!this.returnSchedule() || this.returnSeats().length === 0)
    ) {
      this.bookingState.setActiveLeg('return');
      this.router.navigate(['/search']);
      return;
    }

    if (this.authState.isAuthenticated()) {
      this.passengerId.set(this.authState.session()?.userId ?? null);
      this.prefillFromProfile();
    }

    this.pwdPax.set(Math.max(this.bookingState.pwdPax(), this.minPwd()));
    this.seniorPax.set(Math.max(this.bookingState.seniorPax(), this.minSenior()));

    this.loadTransactionFee();
    this.loadDiscounts();
    this.refreshQuote();
  }

  private loadDiscounts(): void {
    this.bookingApi.listActiveDiscounts('online').subscribe({
      next: (rules) => this.availableDiscounts.set(rules),
      error: () => this.availableDiscounts.set([]),
    });
  }

  private loadTransactionFee(): void {
    const domain = this.tenantService.operator()?.domain;
    if (!domain) {
      return;
    }

    this.operatorApi.getTransactionFeeByDomain(domain).subscribe({
      next: (result) => this.transactionFee.set(result.transactionFee ?? 0),
      error: () => this.transactionFee.set(0),
    });
  }

  private prefillFromProfile(): void {
    this.passengerApi.getProfile().subscribe({
      next: (profile) => {
        this.passengerId.set(profile.id);
        this.firstName.set(profile.firstName ?? '');
        this.lastName.set(profile.lastName ?? '');
        this.email.set(profile.email ?? '');
        this.phone.set(profile.mobile ?? '');
      },
      error: () => {
        const email = this.authState.session()?.email;
        if (email) {
          this.email.set(email);
        }
      },
    });
  }

  applyPromo(): void {
    const code = this.promoCode().trim().toUpperCase();
    this.promoCode.set(code);
    this.refreshQuote(code);
  }

  concessionMin(kind: string): number {
    if (kind === 'pwd') {
      return this.minPwd();
    }
    if (kind === 'senior') {
      return this.minSenior();
    }
    return 0;
  }

  setConcession(kind: 'student' | 'pwd' | 'senior', value: number): void {
    const max = this.selectedSeats().length;
    const minPwd = this.minPwd();
    const minSenior = this.minSenior();
    let student = this.studentPax();
    let pwd = this.pwdPax();
    let senior = this.seniorPax();
    const next = Math.max(0, Math.min(max, Math.floor(value || 0)));

    if (kind === 'student') student = next;
    if (kind === 'pwd') pwd = Math.max(minPwd, next);
    if (kind === 'senior') senior = Math.max(minSenior, next);

    let overflow = student + pwd + senior - max;
    if (overflow > 0 && kind !== 'student') {
      const cut = Math.min(student, overflow);
      student -= cut;
      overflow -= cut;
    }
    if (overflow > 0 && kind !== 'pwd' && pwd > minPwd) {
      const cut = Math.min(pwd - minPwd, overflow);
      pwd -= cut;
      overflow -= cut;
    }
    if (overflow > 0 && kind !== 'senior' && senior > minSenior) {
      const cut = Math.min(senior - minSenior, overflow);
      senior -= cut;
    }

    this.studentPax.set(student);
    this.pwdPax.set(pwd);
    this.seniorPax.set(senior);
    this.refreshQuote(this.appliedPromo() || this.promoCode().trim() || undefined);
  }

  discountLabel(rule: PublicDiscount): string {
    return rule.valueType === 'percent' ? `${rule.value}%` : formatCurrency(rule.value);
  }

  private reservedCount(seats: SeatMapSeat[], type: string): number {
    return seats.filter((seat) => seat.type === type).length;
  }

  private refreshQuote(promoCode?: string): void {
    const outboundPax = this.selectedSeats().length;
    const returnPax = this.returnSeats().length;
    const outboundConcessions = this.concessionsForSeats(this.selectedSeats());
    const inboundConcessions = this.concessionsForSeats(this.returnSeats());
    const code = (promoCode ?? '').trim() || undefined;
    const outbound = this.schedule();
    const inbound = this.returnSchedule();

    const outbound$ = this.bookingApi.quoteDiscount({
      subtotal: this.outboundSubtotal(),
      pax: outboundPax,
      channel: 'online',
      promoCode: code,
      concessions: outboundConcessions,
      routeId: outbound?.routeId,
      scheduleId: outbound?.scheduleId,
    });

    if (this.isRoundTrip() && this.returnSchedule() && returnPax > 0) {
      forkJoin({
        outbound: outbound$,
        inbound: this.bookingApi.quoteDiscount({
          subtotal: this.returnSubtotal(),
          pax: returnPax,
          channel: 'online',
          promoCode: code,
          concessions: inboundConcessions,
          routeId: inbound?.routeId,
          scheduleId: inbound?.scheduleId,
        }),
      }).subscribe({
        next: ({ outbound, inbound }) => this.applyQuotes(code, outbound, inbound),
        error: (err) => this.onQuoteError(err),
      });
      return;
    }

    outbound$.subscribe({
      next: (outbound) => this.applyQuotes(code, outbound),
      error: (err) => this.onQuoteError(err),
    });
  }

  private applyQuotes(
    code: string | undefined,
    outbound: DiscountQuote,
    inbound?: DiscountQuote,
  ): void {
    this.quote.set({
      ...outbound,
      promoDiscount: outbound.promoDiscount + (inbound?.promoDiscount ?? 0),
      concessionDiscount:
        outbound.concessionDiscount + (inbound?.concessionDiscount ?? 0),
      discountAmount: outbound.discountAmount + (inbound?.discountAmount ?? 0),
    });
    this.appliedPromo.set(outbound.promoCode ?? code ?? '');
    this.promoError.set('');
  }

  private onQuoteError(err: { error?: { message?: string | string[] } }): void {
    const message = err.error?.message;
    this.quote.set(null);
    this.appliedPromo.set('');
    this.promoError.set(
      typeof message === 'string'
        ? message
        : Array.isArray(message)
          ? String(message[0])
          : 'Invalid or expired promo code.',
    );
  }

  private concessionsForSeats(seats: SeatMapSeat[]): ConcessionSelection[] {
    const pax = seats.length;
    if (pax === 0) {
      return [];
    }
    const minPwd = this.reservedCount(seats, 'pwd');
    const minSenior = this.reservedCount(seats, 'senior');
    let pwd = Math.max(minPwd, Math.min(this.pwdPax(), pax));
    let senior = Math.max(minSenior, Math.min(this.seniorPax(), pax));
    let student = Math.min(this.studentPax(), pax);
    let overflow = student + pwd + senior - pax;
    if (overflow > 0) {
      const cut = Math.min(student, overflow);
      student -= cut;
      overflow -= cut;
    }
    if (overflow > 0 && pwd > minPwd) {
      const cut = Math.min(pwd - minPwd, overflow);
      pwd -= cut;
      overflow -= cut;
    }
    if (overflow > 0 && senior > minSenior) {
      senior -= Math.min(senior - minSenior, overflow);
    }
    return [
      { type: 'student', pax: student },
      { type: 'pwd', pax: pwd },
      { type: 'senior', pax: senior },
    ].filter((item) => item.pax > 0);
  }

  pay(): void {
    this.error.set('');

    const schedule = this.schedule();
    const criteria = this.criteria();
    const seats = this.selectedSeats();

    if (!schedule || !criteria || seats.length === 0) {
      this.router.navigate(['/']);
      return;
    }

    if (!this.canPay()) {
      if (!this.agreeToTerms()) {
        this.error.set('Please agree to the terms and conditions.');
      } else {
        this.error.set('Please complete the required passenger details.');
      }
      return;
    }

    const outboundPayload = this.buildPayload(
      schedule,
      criteria.date,
      seats,
    );
    const returnSchedule = this.returnSchedule();
    const returnSeats = this.returnSeats();
    const returnPayload =
      this.isRoundTrip() && returnSchedule && criteria.returnDate && returnSeats.length > 0
        ? this.buildPayload(returnSchedule, criteria.returnDate, returnSeats)
        : null;

    this.submitting.set(true);
    this.bookingApi
      .create(outboundPayload)
      .pipe(
        concatMap((outbound) => {
          this.bookingState.setBooking(outbound);
          if (!returnPayload) {
            return of({ outbound, inbound: null as typeof outbound | null });
          }
          return this.bookingApi.create(returnPayload).pipe(
            concatMap((inbound) => of({ outbound, inbound })),
          );
        }),
      )
      .subscribe({
        next: ({ outbound, inbound }) => {
          this.bookingState.setBooking(outbound);
          this.bookingState.setReturnBooking(inbound);
          saveLastBooking(outbound, inbound);
          const origin = window.location.origin;
          const returnQuery = inbound
            ? `&returnRef=${encodeURIComponent(inbound.referenceCode)}`
            : '';
          const successUrl = `${origin}/booking-confirmed?ref=${encodeURIComponent(outbound.referenceCode)}${returnQuery}`;
          const failureUrl = `${successUrl}&payment=failed`;
          this.bookingApi
            .startStarpay(outbound.referenceCode, successUrl, failureUrl)
            .subscribe({
              next: (session) => {
                window.location.assign(session.paymentUrl);
              },
              error: (err) => {
                this.submitting.set(false);
                this.error.set(starpayError(err));
              },
            });
        },
        error: (err) => {
          this.submitting.set(false);
          const outbound = this.bookingState.booking();
          if (outbound && returnPayload) {
            this.error.set(
              `Your departing trip was booked (${outbound.referenceCode}), but the return trip could not be completed. Please try again or contact support.`,
            );
            return;
          }
          if (err?.status === 409) {
            this.error.set(
              'One or more selected seats were just taken. Please pick different seats.',
            );
          } else {
            this.error.set('We could not complete your booking. Please try again.');
          }
        },
      });
  }

  back(): void {
    if (this.isRoundTrip()) {
      this.bookingState.setActiveLeg('return');
    } else {
      this.bookingState.setActiveLeg('outbound');
    }
    this.router.navigate(['/select-seats']);
  }

  private buildPayload(
    schedule: ScheduleAvailability,
    travelDate: string,
    seats: SeatMapSeat[],
  ): CreateBookingRequest {
    return {
      passengerId: this.passengerId() ?? undefined,
      scheduleId: schedule.scheduleId,
      travelDate,
      seats: seats.map((seat) => ({ row: seat.row, column: seat.column })),
      passenger: {
        firstName: this.firstName().trim(),
        lastName: this.lastName().trim(),
        email: this.email().trim(),
        phone: this.phone().trim(),
      },
      promoCode: this.appliedPromo() || undefined,
      concessions: this.concessionsForSeats(seats),
      paymentMethod: this.paymentMethod(),
      deliveryMethod: DELIVERY_METHOD_DEFAULT,
      agreeToTerms: this.agreeToTerms(),
    };
  }
}
